import { useCallback, useMemo, useRef, useState } from 'react'
import { ChevronLeft, ChevronRight, House, MessageSquareText, type LucideIcon } from 'lucide-react'
import { twMerge } from 'tailwind-merge'
import { Button, Menu, Tooltip } from '@highlight/ui'
import { HOME_VIEW_ID, type SidepanelView } from '@renderer/overlay/Sidepanel/view-atoms'
import { useViewNavigation } from '@renderer/overlay/Sidepanel/view-atoms'
import { useHoverIntent } from '@renderer/overlay/Sidepanel/hooks/use-hover-intent'
import { useLongPress } from '@renderer/overlay/Sidepanel/hooks/use-long-press'
import { viewChrome } from '../../view-registry'
import { ACTIVE_TOOLBAR_BUTTON_CLASS } from './toolbar-button-class'
import { AmplitudeRenderer } from '@renderer/utils/amplitude.renderer'

/** One rung of the trail: the view plus the absolute history index to jump to. */
export interface NavTrailEntry {
  index: number
  key: number
  view: SidepanelView
}

/**
 * Home isn't a registry entry, so it's named and iconned here; every View gets
 * both from the registry, which owns whether it titles itself per-document.
 */
function trailLabel(view: SidepanelView): string {
  if (view.id === HOME_VIEW_ID) return 'Home'
  return viewChrome(view)?.trailLabel ?? 'View'
}

function trailIcon(view: SidepanelView): LucideIcon {
  if (view.id === HOME_VIEW_ID) return House
  return viewChrome(view)?.icon ?? MessageSquareText
}

interface SidepanelHeaderNavButtonProps {
  direction: 'back' | 'forward'
  /** Nearest-first, so the menu reads outward from where you are now. */
  trail: Array<NavTrailEntry>
  disabled?: boolean
  onNavigate: () => void
  tooltip: string
  /** Frame the button and show the icon of the view a click lands on beside the chevron. */
  showDestination?: boolean
}

/**
 * A history chevron that doubles as a breadcrumb. Click steps one view, but
 * press-and-hold or right-click opens the whole trail — the browser affordance,
 * because the underlying model is a browser stack (`{ entries, index }`).
 *
 * Hover opens it too, because those press gestures are discoverable in a
 * browser and invisible here, and a trail nobody finds leaves a deep stack five
 * clicks from Home.
 *
 * The button is deliberately *not* a `Menu.Trigger`: a trigger opens on click,
 * and click has to stay a plain one-step navigation.
 */
export function SidepanelHeaderNavButton({
  direction,
  trail,
  disabled,
  onNavigate,
  tooltip,
  showDestination,
}: SidepanelHeaderNavButtonProps) {
  const { jumpToIndex } = useViewNavigation()
  const anchorRef = useRef<HTMLButtonElement | null>(null)
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isTooltipOpen, setIsTooltipOpen] = useState(false)

  // A single-entry trail is exactly what a click already does, so a menu would
  // be one extra gesture for the same outcome.
  const canShowTrail = trail.length > 1

  const openTrail = useCallback(
    (via: 'hover' | 'press') => {
      setIsMenuOpen(true)
      AmplitudeRenderer.trackEvent('Sidepanel Header', { action: `open ${direction} trail`, via })
    },
    [direction],
  )

  const {
    cancel: cancelHoverIntent,
    anchorHandlers: hoverAnchorHandlers,
    popupHandlers: hoverPopupHandlers,
  } = useHoverIntent({
    onOpen: () => openTrail('hover'),
    onClose: () => setIsMenuOpen(false),
    isOpen: isMenuOpen,
    disabled: disabled || !canShowTrail,
  })

  const handleLongPress = useCallback(() => {
    if (!canShowTrail) return
    // A press supersedes a hover already in flight, and its menu stays until
    // dismissed instead of following the pointer away.
    cancelHoverIntent()
    openTrail('press')
  }, [canShowTrail, cancelHoverIntent, openTrail])

  const { isPressed, handlers } = useLongPress({
    onLongPress: handleLongPress,
    onClick: onNavigate,
    disabled,
  })

  const handleMenuOpenChange = useCallback(
    (open: boolean) => {
      setIsMenuOpen(open)
      if (!open) cancelHoverIntent()
    },
    [cancelHoverIntent],
  )

  const handleJump = useCallback(
    (entry: NavTrailEntry) => {
      jumpToIndex(entry.index)
      AmplitudeRenderer.trackEvent('Sidepanel Header', { action: `jump via ${direction} trail` })
    },
    [direction, jumpToIndex],
  )

  const items = useMemo(
    () =>
      trail.map((entry) => {
        const Icon = trailIcon(entry.view)
        return (
          <Menu.Item key={entry.key} onClick={() => handleJump(entry)}>
            <Icon className="opacity-70" />
            <span className="min-w-0 truncate">{trailLabel(entry.view)}</span>
          </Menu.Item>
        )
      }),
    [trail, handleJump],
  )

  const Chevron = direction === 'back' ? ChevronLeft : ChevronRight
  const destination = showDestination ? trail[0] : undefined
  const DestinationIcon = destination && trailIcon(destination.view)

  return (
    // Non-modal because a modal menu lays a backdrop over the button that opened
    // it, which reads as a pointer-leave and would close a hovered trail the
    // instant it appeared.
    <Menu modal={false} open={isMenuOpen} onOpenChange={handleMenuOpenChange}>
      {/* The trail replaces the tooltip on the same hover, so a button that has
          one never shows the other first. Kept mounted rather than swapped out,
          because the wrapper leaving the tree would remount the button. */}
      <Tooltip open={isTooltipOpen && !canShowTrail} onOpenChange={setIsTooltipOpen}>
        {/* A wrapper, not the button: a disabled button gets no pointer events, so the tooltip would never open. */}
        <Tooltip.Trigger render={<div className="relative flex" />}>
          <Button
            ref={anchorRef}
            variant={destination ? 'outline' : 'ghost'}
            shape={destination ? undefined : 'square'}
            disabled={disabled}
            aria-label={tooltip}
            aria-haspopup={canShowTrail ? 'menu' : undefined}
            aria-expanded={canShowTrail ? isMenuOpen : undefined}
            className={twMerge((isPressed || isMenuOpen) && ACTIVE_TOOLBAR_BUTTON_CLASS)}
            {...handlers}
            onPointerEnter={hoverAnchorHandlers.onPointerEnter}
            onPointerDown={(event) => {
              hoverAnchorHandlers.onPointerDown()
              handlers.onPointerDown(event)
            }}
            onPointerLeave={() => {
              hoverAnchorHandlers.onPointerLeave()
              handlers.onPointerLeave()
            }}
          >
            {/* The chevron steps down a size beside a destination so the destination reads first. */}
            <Chevron aria-hidden="true" className={destination ? 'size-3' : undefined} />
            {DestinationIcon && <DestinationIcon aria-hidden="true" />}
          </Button>
        </Tooltip.Trigger>
        <Tooltip.Content side="bottom">{tooltip}</Tooltip.Content>
      </Tooltip>

      {canShowTrail && (
        <Menu.Portal>
          {/* Anchored to the button explicitly, since it isn't a Menu.Trigger. */}
          <Menu.Positioner side="bottom" align="start" anchor={anchorRef}>
            <Menu.Popup className="max-w-64" {...hoverPopupHandlers}>
              {items}
            </Menu.Popup>
          </Menu.Positioner>
        </Menu.Portal>
      )}
    </Menu>
  )
}
